import React, { useState } from 'react';
import {
  RefreshCw, CheckSquare, Square,
  Calendar, Hash, AlertTriangle, Flag, TrendingUp,
} from 'lucide-react';

// ─── Text-Highlighting Helper ─────────────────────────────────────────────────
export const HighlightText = ({ text = '', query = '' }) => {
  if (!query || !query.trim()) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark
            key={i}
            className="bg-yellow-300 dark:bg-yellow-500/60 text-gray-900 dark:text-white rounded-sm px-0.5 font-bold not-italic"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

// ─── Artikel-Zeile ────────────────────────────────────────────────────────────
export const ArticleRow = ({ item, onCheck, updating, isCompleted, isTabak, priceChange = null, highlight = '' }) => {
  const [showQtyInput, setShowQtyInput] = useState(false);
  const [qty, setQty] = useState('');
  const isChecked = item.status === 'checked';
  const hasDiff = item.has_difference;

  const handleToggle = () => {
    if (isCompleted) return;
    if (isChecked) {
      onCheck(item, 'pending', null, null, null);
      setShowQtyInput(false);
      setQty('');
    } else {
      onCheck(item, 'checked', null, null, null);
    }
  };

  const handleQtySubmit = e => {
    e.preventDefault();
    const q = parseInt(qty, 10);
    if (!isNaN(q)) {
      onCheck(item, 'checked', q, null, null);
      setShowQtyInput(false);
      setQty('');
    }
  };

  return (
    <div className={`group transition-all duration-300 ${isChecked ? 'opacity-50' : ''} ${highlight && !isChecked ? 'first:bg-yellow-50/60 dark:first:bg-yellow-900/10 first:rounded-lg' : ''}`} data-testid="article-row">
      <div
        className={`flex items-start gap-3 py-2.5 px-3 rounded-lg transition-colors ${
          !isCompleted
            ? `cursor-pointer active:scale-[0.99] ${
                isTabak
                  ? 'hover:bg-orange-50 dark:hover:bg-orange-900/20'
                  : 'hover:bg-blue-50/40 dark:hover:bg-gray-800/60'
              }`
            : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
        }`}
        onClick={!isCompleted ? handleToggle : undefined}
      >
        {/* Checkbox / Status-Icon – immer sichtbar, kein separater Klick nötig */}
        {isTabak ? (
          <span
            className={`flex-shrink-0 mt-0.5 ${isChecked ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'}`}
            data-testid="article-check-btn"
          >
            {updating === item.id
              ? <RefreshCw className="h-5 w-5 animate-spin text-primary" />
              : isChecked ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
          </span>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); handleToggle(); }}
            disabled={updating === item.id || isCompleted}
            className={`flex-shrink-0 mt-0.5 transition-colors ${isChecked ? 'text-green-500' : 'text-gray-300 dark:text-gray-600 hover:text-primary'} disabled:cursor-default`}
            data-testid="article-check-btn"
          >
            {updating === item.id
              ? <RefreshCw className="h-5 w-5 animate-spin text-primary" />
              : isChecked ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
          </button>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
            <span className={`text-sm font-medium ${isChecked ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
              {/* Fallback: Wenn article_name leer ist oder nur Ziffern enthält → Fremd-Nr anzeigen */}
              {item.article_name && !/^\d+$/.test(item.article_name.trim())
                ? <HighlightText text={item.article_name} query={highlight} />
                : <span className="text-gray-400 dark:text-gray-500 italic text-sm">
                    {item.fremd_article_number
                      ? `Art. ${item.fremd_article_number}`
                      : 'Unbekanntes Produkt'}
                  </span>
              }
            </span>
            {!isTabak && item.is_new_product && !item.ean && !isChecked && (
              <span className="inline-flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-1.5 py-0.5 rounded-full">
                <Hash className="h-3 w-3" /> EAN
              </span>
            )}
            {item.ean && (
              <span className="text-xs text-gray-400 dark:text-gray-600 font-mono">{item.ean}</span>
            )}
            {item.mhd_confirmed && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                <Calendar className="h-3 w-3" /> {item.mhd_confirmed}
              </span>
            )}
            {!isTabak && item.mhd_from_pdf && !item.mhd_confirmed && !isChecked && (
              <span className="inline-flex items-center gap-1 text-xs text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded-full">
                <Calendar className="h-3 w-3" /> MHD: {item.mhd_from_pdf}
              </span>
            )}
            {hasDiff && (
              <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 rounded-full">
                <AlertTriangle className="h-3 w-3" />
                Diff: {item.difference_amount > 0 ? '+' : ''}{item.difference_amount}
              </span>
            )}
            {/* NEUPREIS-Badge für Tabak */}
            {isTabak && priceChange && !isChecked && (
              <span
                className="inline-flex items-center gap-1 text-xs font-bold text-white bg-orange-500 px-2 py-0.5 rounded-full"
                title={`War: ${priceChange.old_price.toFixed(2)} € → Jetzt: ${priceChange.new_price.toFixed(2)} €\nAlte Ware zuerst verkaufen!`}
              >
                <TrendingUp className="h-3 w-3" />
                NEUPREIS {priceChange.diff >= 0 ? '+' : '−'}{Math.abs(priceChange.diff).toFixed(2)} €
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="font-mono"><HighlightText text={item.fremd_article_number} query={highlight} /></span>
            {item.meh && <span>{item.meh}</span>}
            <span>{item.gel || item.delivered_quantity || item.best || item.ordered_quantity} &times; {item.inhalt} = <strong className="text-gray-700 dark:text-gray-300">{item.gesamt_menge}</strong></span>
            {item.kvp != null && <span>{Number(item.kvp).toFixed(2).replace('.', ',')} &euro;</span>}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {item.pos && <span className="text-xs text-gray-400 dark:text-gray-600 w-6 text-right">{item.pos}</span>}
          {!isChecked && !isCompleted && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowQtyInput(!showQtyInput); }}
              className="text-amber-400 hover:text-amber-600 dark:text-amber-500 dark:hover:text-amber-300 transition-colors"
              title="Menge abweichend melden"
              data-testid="article-diff-btn"
            >
              <Flag className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {showQtyInput && !isChecked && (
        <form onSubmit={handleQtySubmit} className="flex items-center gap-2 ml-11 mb-1.5">
          <input
            type="number" min="0" value={qty} onChange={e => setQty(e.target.value)}
            placeholder={`Soll: ${item.gesamt_menge}`} autoFocus
            className="w-32 px-2 py-1 text-sm border border-amber-300 dark:border-amber-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-300"
            data-testid="article-qty-input"
          />
          <button type="submit" className="px-3 py-1 text-xs bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors" data-testid="article-qty-submit">OK</button>
          <button type="button" onClick={() => { setShowQtyInput(false); setQty(''); }} className="text-xs text-gray-400 hover:text-gray-600">&times;</button>
        </form>
      )}
    </div>
  );
};
